import React, { useEffect, useMemo, useState } from 'react'
import { Form } from 'react-bootstrap'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { useGetEntitiesQuery } from '../../store/integrations/integrations'
import { Button } from '../../components/Button/Button'
import { integrationObjectRoutingKey, MappedIntegrationObject } from './utils'

const SEARCH_DEBOUNCE_MS = 300
const RECORD_LIST_LIMIT = 25

interface IntegrationObjectRecordPickerProps {
  object: MappedIntegrationObject
  contactExternalId: string | null
  contactName: string | null
  targetRecordId: string | null | undefined
  onSelectRecord: (recordId: string) => void
  onChooseCreateNew: () => void
  onReviewUpdates: () => void
  canReview: boolean
}

export const IntegrationObjectRecordPicker: React.FC<IntegrationObjectRecordPickerProps> = ({
  object,
  contactExternalId,
  contactName,
  targetRecordId,
  onSelectRecord,
  onChooseCreateNew,
  onReviewUpdates,
  canReview,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const [searchInput, setSearchInput] = useState<string>('')
  const [debouncedSearch, setDebouncedSearch] = useState<string>('')

  const needsContactFirst = object.contactRequired && !contactExternalId

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchInput])

  const { data, isFetching } = useGetEntitiesQuery(
    {
      accountId,
      connector: object.serviceId,
      entityType: integrationObjectRoutingKey('list', object.objectName),
      contactExternalId,
      search: debouncedSearch,
      limit: RECORD_LIST_LIMIT,
    },
    { skip: !object.allowUpdate || needsContactFirst },
  )

  const items = useMemo(() => data?.items ?? [], [data])

  const reviewButton = (
    <Button variant='primary' className='w-100 zocks-mt-2.5' onClick={onReviewUpdates} disabled={!canReview}>
      Review updates
    </Button>
  )

  if (needsContactFirst) {
    return (
      <div className='integration-object-record-picker'>
        <div className='paragraph-small zocks-text-gray-500 p-2'>Select a contact above to see matching records.</div>
        {reviewButton}
      </div>
    )
  }

  return (
    <div className='integration-object-record-picker'>
      {object.allowUpdate ? (
        <div className='mb-3'>
          <div className='integration-object-record-picker__section-label mb-1'>Choose from existing records</div>
          <div className='position-relative'>
            <Form.Control
              className='integration-object-record-picker__search'
              placeholder={`Search ${object.displayName}`}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <i className='bi bi-search integration-object-record-picker__search-icon' />
          </div>
        </div>
      ) : null}

      {object.allowUpdate && !isFetching ? (
        <div className='paragraph-small zocks-text-gray-600 mb-2'>
          {data?.totalCount ?? items.length} {(data?.totalCount ?? items.length) === 1 ? 'result' : 'results'}
        </div>
      ) : null}

      <div className='integration-object-record-picker__list'>
        {object.allowUpdate && isFetching ? <div className='paragraph-small zocks-text-gray-500 p-2'>Loading records…</div> : null}

        {object.allowUpdate && !isFetching && items.length === 0 ? <div className='paragraph-small zocks-text-gray-500 p-2'>No records found.</div> : null}

        {object.allowUpdate
          ? items.map((record) => {
              const primaryLine = record.referenceValues[0]?.value
              const secondaryLine = record.referenceValues[1]?.value
              const selected = targetRecordId === record.id
              return (
                <Button
                  key={record.id}
                  className={`integration-object-record-picker__row ${selected ? 'is-selected' : ''}`}
                  onClick={() => onSelectRecord(record.id)}
                >
                  <span className='integration-object-record-picker__lines'>
                    <span className='integration-object-record-picker__primary' title={primaryLine || '(untitled)'}>
                      {primaryLine || '(untitled)'}
                    </span>
                    {secondaryLine ? (
                      <span className='integration-object-record-picker__secondary' title={secondaryLine}>
                        {secondaryLine}
                      </span>
                    ) : null}
                  </span>
                  {selected ? <i className='bi bi-check-lg integration-object-record-picker__check' /> : null}
                </Button>
              )
            })
          : null}
      </div>

      {object.allowCreate ? (
        <>
          <div className='integration-object-record-picker__divider'>
            <span>or</span>
          </div>
          <div className='integration-object-record-picker__section-label mb-1'>Create a new object{contactName ? ` for ${contactName}` : ''}</div>
          <Button variant='primaryGhost' className='w-100' onClick={onChooseCreateNew}>
            + Create new {object.displayName}
          </Button>
        </>
      ) : null}

      {reviewButton}
    </div>
  )
}
